import { loadDeferredSentryIntegrations } from './instrument';

import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, type NavigateOptions, type To } from 'react-router-dom';

import DetectBrowser from 'components/error/DetectBrowser';
import ErrorBoundary from 'components/error/ErrorBoundary/ErrorBoundary';
import { GenericError } from 'components/error/ErrorAlert/ErrorAlert';
import LoadingSpinner from 'components/data/LoadingSpinner/LoadingSpinner';
import AuthProvider from 'providers/AuthProvider';
import { isProductionBuild } from 'globalUtils';

import './custom.scss';

/*
  Only Data/Framework mode returns a promise from navigate(); the <BrowserRouter>
  below is Declarative mode, so the promise half of `void | Promise<void>` is
  unreachable and would otherwise trip no-floating-promises at every call site.
  Narrowing it is react-router's documented fix — see "Return Type Augmentation"
  at https://reactrouter.com/api/hooks/useNavigate.
  Keep this in sync if the app ever moves to createBrowserRouter/RouterProvider.
*/
declare module 'react-router' {
  interface NavigateFunction {
    (to: To, options?: NavigateOptions): void;
    (delta: number): void;
  }
}

// The purpose of dynamically importing the App component here is to reduce
// the size of the initial bundle. Doing this results in a much faster
// first contentful paint.
const App = lazy(() => import('./App'));

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found.');
}

const root = createRoot(container, {
  onCaughtError: Sentry.reactErrorHandler(),
  onUncaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <React.StrictMode>
    <DetectBrowser>
      <ErrorBoundary fallback={<GenericError isFullScreen />}>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<LoadingSpinner isFullScreen />}>
              <App />
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </DetectBrowser>
  </React.StrictMode>
);

// oxlint-disable-next-line eslint/no-void, unicorn/prefer-top-level-await -- Top-level await would delay entry-module completion.
void loadDeferredSentryIntegrations().catch((error: unknown) => {
  Sentry.captureException(error);
});

if (isProductionBuild) {
  const stopSign = String.fromCodePoint(0x1f6d1);
  // oxlint-disable-next-line no-console
  console.log(
    `%c${stopSign}%cStop!`,
    'font-size: 3rem; margin-right: 0.5rem;',
    'font-size: 3rem; font-weight: bold; color: red;'
  );
  // oxlint-disable-next-line no-console
  console.log(
    '%cThis is a browser feature intended for developers. If someone told you to copy-paste something here, it is a scam and could compromise your account. Please forward any instructions you receive to: support@rsilogistics.com',
    'font-size: 1.5rem;'
  );
}
